import { CRORE, toTitleCase } from '../../../../../../utils/utils';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../utils/dateUtils';
import { ENTITY_VISUAL_TYPE } from '../../../../../../utils/constants/commonConstants';
import { formatShareholdingPercent } from '../../../../../../utils/formatShareholdingPercent';
import { toNumberOrNull } from '../../../../../../store/dashboard/compliancesBefe/mappers';
import { formatFyFromEndYear, formatRptAmount } from '../../../../../../store/dashboard/relatedParties/relatedPartyTransactions_mappers';
import type { AicaStatus, EntityDetails } from '../../../../../../store/dashboard/relatedParties/relatedPartyTransactions.types';
import {
    ENTITY_HOVER_LABELS as L,
    ENTITY_STATUS_ACTIVE,
    ENTITY_STATUS_PILL,
    FINANCIALS_LABEL,
    FINANCIALS_NOT_AVAILABLE,
    ROW_DETAILS_LOCKED,
    ROW_DETAILS_SEPARATOR,
    SECOND_DEGREE,
} from './EntityHoverCard.constants';
import type { EntityHoverProfile, HoverFinancialValue, HoverStatRow, RowFinancialsDetails } from './EntityHoverCard.types';
import { RPT_EMPTY_VALUE as EMPTY } from '../../RelatedPartyTransactions/relatedPartyTransactions_constants';

export interface EntityHoverRow {
    fromKid?: string;
    toKid?: string;
    fromName?: string;
    degree?: string;
    edgeType?: string;
    type?: string;
    edgeValue?: string;
    shareholdingPercentage?: number | null;
    entityDetails?: EntityDetails;
    aicaStatus?: AicaStatus;
}

const LLP_TYPES = new Set(['LLP', 'LIMITED_LIABILITY_PARTNERSHIP']);

const hasText = (value: string | null | undefined): value is string => !!value?.trim();

/** INR short form with the rupee sign after any minus: 4500000000 → "₹450.00 Cr", null → "—". */
const formatHoverMoney = (value: unknown): string => {
    const amount = toNumberOrNull(value);
    if (amount === null) return EMPTY;
    return amount < 0 ? `-₹${formatRptAmount(-amount)}` : `₹${formatRptAmount(amount)}`;
};

const formatPlainAmount = (value: unknown): string => {
    const amount = toNumberOrNull(value);
    return amount === null ? EMPTY : formatRptAmount(amount);
};

const formatHoverDate = (value: string | null | undefined): string =>
    formatDateByFormat(value ?? undefined, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) || EMPTY;

const humaniseEdgeType = (edgeType?: string): string => (edgeType ? toTitleCase(edgeType.replace(/_/g, ' ')) : EMPTY);

const toWebsite = (url: string | null | undefined): { href: string; text: string } | null => {
    if (!hasText(url)) return null;
    const trimmed = url.trim();
    const href = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    if (!href.toLowerCase().startsWith('https:')) return null;
    return { href, text: href.replace(/^https:\/\//i, '').replace(/\/$/, '') };
};

const financialValues = (financials: AicaStatus['financials'], netWorthLabel: string): HoverFinancialValue[] => [
    { label: L.REVENUE, value: formatHoverMoney(financials?.revenue) },
    { label: L.PAT, value: formatHoverMoney(financials?.pat) },
    { label: netWorthLabel, value: formatHoverMoney(financials?.netWorth) },
];

const canShowCta = (aica: AicaStatus, kid: string): boolean => (aica.state === 'ADD' ? !!kid : !!aica.aicaOrgId);

const formatInvestors = (investors: string[] | null | undefined): string | null => {
    const names = (investors ?? []).filter(hasText);
    if (!names.length) return null;
    return names.length === 1 ? names[0] : `${names[0]} (+${names.length - 1} more)`;
};

/** The row's own CIN/LLPIN: degree-2 director-network rows carry it on toKid. */
const entityKidOf = (row: EntityHoverRow): string => (row.degree === SECOND_DEGREE ? row.toKid : row.fromKid) ?? '';

/** Hover-card shareholding without trailing zeros: 30.00 → "30%", 30.50 → "30.5%". */
const formatHoverShareholding = (value: number | null | undefined): string =>
    formatShareholdingPercent(value, { nullText: EMPTY }).replace(/(\.\d*[1-9])0+%$|\.0+%$/, '$1%');

/** polus leaves shareholdingPercentage null on JV and LLP rows, whose edgeValue still carries the % ("50", "60.00%"). */
const edgeValuePercent = (edgeValue: string | undefined): number | null => toNumberOrNull(edgeValue?.trim().replace(/%$/, ''));

/** FY label of a VIEW org's audited financials; null when not VIEW or no audited year (financials null or all-null). */
const viewFinancialYearLabel = (aica: AicaStatus | undefined): string | null =>
    aica?.state === 'VIEW' ? formatFyFromEndYear(aica.financials?.financialYear) : null;

/** Same text as the Funding History total row (SummaryRow): 455000000 → "45.50 Cr". */
const formatFundingTotal = (amount: number): string => `${(amount / CRORE).toFixed(2)} Cr`;

export function toEntityHoverProfile(row: EntityHoverRow): EntityHoverProfile {
    const details = row.entityDetails ?? {};
    const aica = row.aicaStatus;
    const kid = entityKidOf(row);
    const isLlp = details.entityCategory?.toLowerCase() === 'llp' || LLP_TYPES.has(String(row.type ?? '').toUpperCase());
    const rawName = hasText(details.legalName) ? details.legalName : row.fromName ?? '';
    const isView = aica?.state === 'VIEW';
    const website = isView ? toWebsite(aica?.websiteUrl) : null;
    const status = details.status?.trim();
    const isActive = status?.toUpperCase() === ENTITY_STATUS_ACTIVE;

    const detailRows: HoverStatRow[] = [
        { label: L.INCORPORATION_DATE, value: formatHoverDate(details.incorporationDate) },
        status
            ? {
                  label: L.ENTITY_STATUS,
                  value: isActive ? ENTITY_STATUS_PILL.ACTIVE : ENTITY_STATUS_PILL.INACTIVE,
                  asPill: true,
                  tone: isActive ? 'positive' : 'negative',
              }
            : { label: L.ENTITY_STATUS, value: EMPTY },
    ];
    const fundingRaised = isView ? toNumberOrNull(aica?.financials?.fundingRaised) : null;
    if (fundingRaised !== null) detailRows.push({ label: L.FUNDING_RAISED, value: formatFundingTotal(fundingRaised), compact: true });
    const investors = isView ? formatInvestors(aica?.financials?.investors) : null;
    if (investors) detailRows.push({ label: L.INVESTORS, value: investors, compact: true });
    if (hasText(details.activeCompliance)) {
        const compliance = details.activeCompliance.trim();
        detailRows.push({
            label: L.ACTIVE_COMPLIANCE,
            value: compliance,
            ...(/^active/i.test(compliance) && { tone: 'positive' as const }),
        });
    }
    detailRows.push(
        isLlp
            ? { label: L.TOTAL_OBLIGATION, value: formatPlainAmount(details.totalObligationOfContribution) }
            : { label: L.PAID_UP_CAPITAL, value: formatPlainAmount(details.paidUpCapital) },
        { label: L.SUM_OF_CHARGES, value: formatPlainAmount(details.sumOfCharges) },
        { label: isLlp ? L.LLPIN : L.CIN, value: kid || EMPTY, ...(status && !isActive && { tag: status }), identifier: true },
        { label: L.NEXT_CIN, value: hasText(details.nextCin) ? details.nextCin : EMPTY },
    );
    if (hasText(details.financialYear)) detailRows.push({ label: L.FINANCIAL_YEAR, value: details.financialYear });
    if (hasText(details.cirpStatus)) detailRows.push({ label: L.CIRP_STATUS, value: details.cirpStatus });

    const appointment = (row.degree === SECOND_DEGREE ? details.appointment : null) ?? {};
    if (hasText(appointment.designation)) detailRows.push({ label: L.DESIGNATION, value: appointment.designation });
    if (hasText(appointment.dateOfAppointment))
        detailRows.push({ label: L.DATE_OF_APPOINTMENT, value: formatHoverDate(appointment.dateOfAppointment) });
    if (hasText(appointment.dateOfAppointmentCurrentDesignation))
        detailRows.push({ label: L.CURRENT_DESIGNATION_DATE, value: formatHoverDate(appointment.dateOfAppointmentCurrentDesignation) });
    if (hasText(appointment.dateOfCessation))
        detailRows.push({ label: L.APPOINTMENT_STATUS, value: `Former (ceased ${formatHoverDate(appointment.dateOfCessation)})` });

    const fyLabel = viewFinancialYearLabel(aica);
    const financials = !aica
        ? null
        : fyLabel
        ? { label: `${FINANCIALS_LABEL} (${fyLabel})`, values: financialValues(aica.financials, L.NET_WORTH) }
        : { label: FINANCIALS_NOT_AVAILABLE, values: null };

    const ctaAllowed = !!aica && canShowCta(aica, kid);

    return {
        name: toTitleCase(rawName) || EMPTY,
        city: hasText(details.city) ? toTitleCase(details.city) : null,
        websiteHref: website?.href ?? null,
        websiteText: website?.text ?? null,
        financials,
        relationRows: [
            { label: L.RELATIONSHIP, value: humaniseEdgeType(row.edgeType) },
            { label: L.SHAREHOLDING, value: formatHoverShareholding(row.shareholdingPercentage ?? edgeValuePercent(row.edgeValue)) },
        ],
        detailRows,
        cta: ctaAllowed ? { state: aica.state, aicaOrgId: aica.aicaOrgId, prefillName: rawName, prefillIdentifier: kid } : null,
    };
}

/** Details-column content for a non-person row: VIEW with a financial year → the card's Revenue / PAT / Net-Worth; UNLOCK → "Locked"; else null. */
export function toRowFinancialsDetails(aicaStatus: AicaStatus | undefined, entityType: string | undefined): RowFinancialsDetails | null {
    if (entityType?.toUpperCase() === ENTITY_VISUAL_TYPE.PERSON) return null;
    if (aicaStatus?.state === 'UNLOCK') return { kind: 'locked', text: ROW_DETAILS_LOCKED };
    if (!aicaStatus || !viewFinancialYearLabel(aicaStatus)) return null;
    const values = financialValues(aicaStatus.financials, L.ROW_NET_WORTH);
    return { kind: 'financials', values, text: values.map(({ label, value }) => `${label} ${value}`).join(ROW_DETAILS_SEPARATOR) };
}
